import { toOnlyNumbersAndSpacesAndDashes } from '@/infrastructure/helper';
import { LeadAPI, LeadCustomFieldAPI, StaticProfileConfig } from '@lemontaps/lib/types/api';
import { LeadCustomFieldValueType, ThemeBoxStyle } from '@lemontaps/lib/types/global';
import { getContactFormTranslationKeyByField, validateEmail } from '@lemontaps/lib/util';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ThemeOverride from './ThemeOverride';
import { useMuiTheme } from '@/config/theme/useMuiTheme';
import ProfileThemeProvider from './ProfileThemeProvider';
import LtLoadingButton from './LtLoadingButton';
import { SingleCustomFieldRenderer } from './LeadGen/LeadForm/components/LeadCustomFieldsRenderer';
import { customCssClasses } from '@lemontaps/lib/constants/profile-page/customCssClassNames';
import ClearableTextField from './ClearableTextField';

type Props = {
  onSubmit?: (values: Partial<LeadAPI>) => Promise<boolean>;
  boxStyle?: ThemeBoxStyle;
  leadCustomFields: LeadCustomFieldAPI[];
  disabled?: boolean;
} & Omit<NonNullable<StaticProfileConfig['leadGen']>, 'flow'>;

export const ContactForm = ({
  fields,
  labels,
  privacyPolicyUrl,
  submitButtonColor,
  boxStyle,
  onSubmit,
  leadCustomFields,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Partial<LeadAPI>>({});
  const [acknowledged, setAcknowledged] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof LeadAPI | 'acknowledgement' | number, string>>
  >({});

  const updateValidations = useCallback(
    (
      updatedValues: null | typeof values = null,
      acknowledgedLocal: null | boolean = null,
      forceAll = false,
    ) => {
      const valuesToUse = updatedValues ?? values;
      const acknowledgedToUse = acknowledgedLocal ?? acknowledged;
      let hasValidationErrors = false;
      setValidationErrors(prevValidationErrors => {
        const fieldsToIterate = forceAll
          ? fields
          : Object.keys(prevValidationErrors).map(f => fields.find(field => field.name === f));
        const updatedValidationErrors = {} as Partial<
          Record<keyof LeadAPI | 'acknowledgement', string>
        >;
        for (const _field of fieldsToIterate) {
          const fieldName = _field?.name as keyof typeof updatedValidationErrors;
          const isCustom = _field?.isCustom;
          // required fields validations

          const isRequired = _field?.required;

          if (isRequired) {
            if (isCustom) {
              const customFieldValues = valuesToUse.leadCustomFieldValues || [];
              const customFieldValue = customFieldValues.find(
                item => item.typeId === _field?.name,
              )?.value;
              if (!customFieldValue) {
                updatedValidationErrors[fieldName] = t('error.requiredFieldGeneral');
              }
            } else {
              if (!(valuesToUse[fieldName as keyof LeadAPI] as string)?.trim()) {
                updatedValidationErrors[fieldName] = t('error.requiredFieldGeneral');
              }
            }
          }

          // field format validations
          switch (fieldName) {
            case 'email':
            case 'workEmail':
              if (valuesToUse[fieldName] && !validateEmail(valuesToUse[fieldName])) {
                updatedValidationErrors[fieldName] = t('error.invalidFieldGeneral');
              }
              break;
            case 'postCode':
            case 'mobilePhone':
            case 'workFax':
            case 'workPhone':
              if (valuesToUse[fieldName] && typeof Number(valuesToUse[fieldName]) !== 'number') {
                updatedValidationErrors[fieldName] = t('error.invalidFieldGeneral');
              }
              break;
          }
        }

        // acknowledgement is required
        if ((forceAll || prevValidationErrors['acknowledgement']) && !acknowledgedToUse) {
          updatedValidationErrors['acknowledgement'] = t('error.requiredFieldGeneral');
        }

        hasValidationErrors = Object.values(updatedValidationErrors).some(Boolean);
        return updatedValidationErrors;
      });
      return hasValidationErrors;
    },
    [acknowledged, fields, t, values],
  );

  const handleInputChange = (key: keyof LeadAPI, value: string) => {
    let val = value;
    if (key === 'workPhone' || key === 'workFax' || key === 'mobilePhone') {
      val = toOnlyNumbersAndSpacesAndDashes(value);
    }

    const updatedValues = { ...values, [key]: val };
    setValues(updatedValues);
    updateValidations(updatedValues);
  };

  const handleSubmit = useCallback(async () => {
    if (!onSubmit) return;

    const hasValidationErrors = updateValidations(values, acknowledged, true);

    if (Object.values(values).every(val => !val) || hasValidationErrors) {
      return;
    }

    setSubmitLoading(true);
    const success = await onSubmit(values);
    if (success) {
      setValues({});
      setAcknowledged(false);
      setValidationErrors({});
    }
    setSubmitLoading(false);
  }, [acknowledged, onSubmit, updateValidations, values]);

  const handleCustomFieldChange = (value: LeadCustomFieldValueType, typeId: number) => {
    const currentFieldValues = values.leadCustomFieldValues || [];
    const currentFieldValue = currentFieldValues.find(item => item.typeId === typeId);
    let newValue = null;
    if (currentFieldValue) {
      newValue = currentFieldValues.map(item =>
        item.typeId === typeId ? { typeId, value: value, id: item.id } : item,
      );
    } else {
      newValue = [...currentFieldValues, { typeId, value: value, id: null }];
    }
    setValues({ ...values, leadCustomFieldValues: newValue });
  };

  const { theme: baseTheme } = useMuiTheme();
  const submitButtonColorTheme = useMemo(
    () => ({
      palette: {
        primaryButton: baseTheme?.palette?.augmentColor?.({
          color: { main: submitButtonColor },
        }),
      },
    }),
    [baseTheme?.palette, submitButtonColor],
  );

  const metaTheme = useMemo(() => ({ boxStyle }), [boxStyle]);

  return (
    <ProfileThemeProvider metaTheme={metaTheme}>
      <Box>
        <Typography variant='h4'>{labels.title}</Typography>
        <Box display='flex' flexDirection='column' mt={2}>
          {fields.map(({ name: _name, required, isCustom }) => (
            <Box key={_name} mb={1.6}>
              {(() => {
                if (isCustom) {
                  const fieldType = leadCustomFields.find(field => field.id === _name);
                  if (!fieldType) return null;
                  const leadCustomFieldValue = values.leadCustomFieldValues?.find(option => {
                    return option.typeId === fieldType.id;
                  });
                  return (
                    <SingleCustomFieldRenderer
                      lead={values}
                      required={required}
                      key={fieldType.id}
                      leadCustomFieldValue={leadCustomFieldValue}
                      onChange={handleCustomFieldChange}
                      fieldType={fieldType}
                      errorMessage={validationErrors[fieldType.id]}
                    />
                  );
                }

                const name = _name as keyof LeadAPI;
                const isMultiline = name === 'notes';
                const commonProps = {
                  key: name,
                  label: `${labels.field[name] || t(getContactFormTranslationKeyByField(name))}`,
                  fullWidth: true,
                  required,
                };

                if (name === 'createdOn') {
                  return (
                    <ClearableTextField
                      InputLabelProps={{ shrink: true }}
                      value={values[name] ?? ''}
                      type='datetime-local'
                      label={`${labels.field[name] || t(getContactFormTranslationKeyByField(name))}`}
                      onChange={e => handleInputChange(name, e.target.value)}
                      fullWidth
                      required={required}
                    />
                  );
                }

                if (name === 'birthday') {
                  return (
                    <ClearableTextField
                      InputLabelProps={{ shrink: true }}
                      value={values[name] ?? ''}
                      type='date'
                      label={`${labels.field[name] || t(getContactFormTranslationKeyByField(name))}`}
                      onChange={e => handleInputChange(name, e.target.value)}
                      fullWidth
                      required={required}
                    />
                  );
                }
                return (
                  <TextField
                    {...commonProps}
                    value={values[name] || ''}
                    onChange={e => handleInputChange(name, e.target.value)}
                    multiline={isMultiline}
                    rows={isMultiline ? 4 : 1}
                    error={!!validationErrors[name]}
                    helperText={validationErrors[name]}
                    className={customCssClasses.contactFormBox.input.className}
                  />
                );
              })()}
            </Box>
          ))}
        </Box>
        <Box
          position='relative'
          mt={2}
          className={customCssClasses.contactFormBox.consent.className}
        >
          <FormControl required error={!!validationErrors['acknowledgement']}>
            <FormControlLabel
              sx={{ textIndent: '2.5rem', margin: '0', position: 'relative' }}
              control={
                <Checkbox
                  className={customCssClasses.contactFormBox.consentCheckbox.className}
                  checked={acknowledged}
                  onChange={() => {
                    setAcknowledged(!acknowledged);
                    updateValidations(values, !acknowledged);
                  }}
                  sx={{ position: 'absolute', top: '-1.2rem', left: '-1.2rem' }}
                  disableRipple
                  color='secondary'
                  aria-label={t('ariaGdprConsent')}
                />
              }
              label={
                <Typography
                  variant='body2'
                  color='secondary'
                  className={customCssClasses.contactFormBox.consentCheckboxLabel.className}
                >
                  <Trans
                    i18nKey={`${labels.gdprConsent} *`}
                    components={[
                      privacyPolicyUrl ? (
                        <Link href={privacyPolicyUrl} target='_blank' rel='noreferrer' />
                      ) : (
                        <span style={{}} />
                      ),
                    ]}
                  />
                </Typography>
              }
            />
            {validationErrors['acknowledgement'] && (
              <FormHelperText>{t('consentToData')}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box width='60%' m='auto' mt={2.5}>
          <ThemeOverride overrides={submitButtonColorTheme}>
            <LtLoadingButton
              fullWidth
              onClick={handleSubmit}
              loading={submitLoading}
              disabled={disabled}
            >
              {labels.submitButton}
            </LtLoadingButton>
          </ThemeOverride>
        </Box>
      </Box>
    </ProfileThemeProvider>
  );
};
